import {GameVariant, Nullable} from "./caos-def";
import {allVariants, SetVariantFunc} from "./utils.variants";
import {calculateUrlHash, getHashParameters, getUrlQueryParameters, getUrlParameter, setHash} from "./utils.url";
import {getValue} from "./utils.objects";
import {levenshtein} from "./util.levenshtein";


export type HashParameters = { [key: string]: HashParameterValue };
export type HashParameterValue = string | string[] | { [key: string]: string };

export type MutableHashParameters = { [key: string]: MutableHashParameterValue };
export type MutableHashParameterValue = string | number | boolean | string[] | { [key: string]: string }

let _setVariant: SetVariantFunc = () => null;

let _getVariant: () => Nullable<GameVariant> = () => null;

let _setSeaMonkeys: (enable: Nullable<boolean>) => void = () => null;

let lastHash = "";

const hashChangeTimeoutDelay = 300;
let hashTimeoutHandle = -1;

export function initNavigation(
    getVariant: () => Nullable<GameVariant>,
    setVariant: SetVariantFunc,
    setSeaMonkeys: (enable: Nullable<boolean>) => void
) {

    // Get Variant Getter/Setter
    _getVariant = getVariant;
    _setVariant = setVariant;

    // Get sea monkeys setter
    _setSeaMonkeys = setSeaMonkeys;

    // Init hash change handler
    initHashChange();
    setTimeout(onHashChange, 5);
}

function onHashChange() {
    clearTimeout(hashTimeoutHandle);
    hashTimeoutHandle = window.setTimeout(_onHashChange, hashChangeTimeoutDelay)
}


function _onHashChange() {

    const rawHash = window.location.hash && window.location.hash[0] === '#'
        ? window.location.hash.substring(1)
        : window.location.hash;

    if (rawHash == lastHash) {
        return;
    }
    lastHash = rawHash;

    const hashParameters: HashParameters = getHashParameters(rawHash);
    const queryParameters: HashParameters = getUrlQueryParameters();

    updateSeaMonkeysFromUrlParameters(hashParameters, queryParameters);
    updateVariantFromUrlParameters(rawHash, hashParameters, queryParameters);

}

function updateVariantFromUrlParameters(rawhash: string, ...hashParameters: HashParameters[]): void {
    let variant: Nullable<GameVariant> = getVariantRaw(rawhash)
        ?? getVariantFromHashParams(...hashParameters);


    if (variant) {
        variant = variant.toUpperCase() as Nullable<GameVariant>;
    }

    // Ensure variant is not already set to this value
    if (variant === _getVariant()) {
        return;
    }

    // Set regardless, as if its null it should be all commands
    _setVariant(variant, false);
}


export function setVariantInURL(variant: Nullable<GameVariant>): void {
    // Get current parameters if any
    const hashParameters = getHashParameters();

    if (window.location.port !== "80") {
        setVariantInHash(variant, hashParameters);
    } else {
        setVariantInUrlPath(variant, hashParameters);
    }
}

function setVariantInHash(variant: Nullable<GameVariant>, hashParameters: HashParameters): void {

    const lastVariant = (typeof hashParameters["variant"] !== "undefined")
        ? (<string>hashParameters["variant"]).toUpperCase() as GameVariant
        : null;

    if (variant?.toUpperCase() == lastVariant?.toUpperCase) {
        return;
    } else if (variant == null) {
        delete hashParameters["variant"];
    } else {
        hashParameters["variant"] = variant.toLowerCase();
    }

    setHash(hashParameters);
}

function setVariantInUrlPath(variant: Nullable<GameVariant>, hashParameters?: HashParameters) {

    // Get current variant in URL;
    const currentVariant = getValue(hashParameters, "variant") as Nullable<GameVariant>;

    if (currentVariant === variant) {
        return;
    }

    // Get path components to remove last variant if set
    const components = getUrlPathComponents();

    // Get last path component as it may be variant
    const lastComponent = components.pop()?.toUpperCase();

    // Make sure last path component is not this variant
    if (lastComponent === variant) {
        return;
    }

    // If last component is not a variant, add it back in
    if (lastComponent && getVariantRaw(lastComponent) == null) {
        // Add back the last component as it is not a variant
        components.push(lastComponent);
    }

    // If variant is non-null/not-empty, lowercase it for URL
    if (variant) {
        components.push(variant.toLowerCase());
    }

    // Get new HASH without variant
    let hash = window.location.hash;
    if (hash && hash.indexOf("variant=") >= 0) {
        if (!hashParameters) {
            hashParameters = getHashParameters(hash);
        }

        // Variant exists in hash, so recalculate hash
        if (typeof hashParameters["variant"] !== "undefined") {
            delete hashParameters["variant"];
            hash = calculateUrlHash(hashParameters);
        }
    }

    if (hash.length > 0) {
        hash = "#" + hash;
    }

    window.history.pushState(null, "", components.join("/") + hash);
}

export function getUseSeaMonkeysFromUrl(...hashParameters: HashParameters[]): Nullable<boolean> {

    const seaMonkeyRaw: Nullable<HashParameterValue> = getUrlParameter(
        seaMonkeysUrlKey,
        ...hashParameters
    );
    return seaMonkeyRaw != null ? seaMonkeyRaw === "true" : null;
}

function updateSeaMonkeysFromUrlParameters(...hashParameters: HashParameters[]) {
    const enable = getUseSeaMonkeysFromUrl(...hashParameters);
    _setSeaMonkeys(enable);
}

const seaMonkeysUrlKey = "seamonkeys";

export function setSeaMonkeysInUrl(enable: Nullable<boolean>) {
    const parameters: MutableHashParameters = getHashParameters();

    const oldValue = typeof parameters[seaMonkeysUrlKey] !== "undefined"
        ? parameters[seaMonkeysUrlKey] === "true"
        : null;

    if (oldValue === enable) {
        return;
    }

    if (enable == null) {
        delete parameters[seaMonkeysUrlKey];
    } else {
        parameters[seaMonkeysUrlKey] = enable;
    }

    setHash(parameters);
}

/**
 * Gets the initial variant from this URL.
 * searching in order: hash, query parameters, and finally, last path component
 * @param hashParameters
 */
export function getInitialVariantFromUrl(...hashParameters: HashParameters[]): Nullable<GameVariant> {
    if (hashParameters.length === 0) {
        hashParameters = [
            getHashParameters(),
            getUrlQueryParameters()
        ];
    }
    for (const parameters of hashParameters) {
        const variant = getVariantFromHashParams(parameters);
        if (variant != null) {
            return variant;
        }
    }
    return getVariantFromUrlPath();
}


function getVariantFromUrlPath(): Nullable<GameVariant> {

    const lastPathComponent = getUrlPathComponents().pop();

    if (lastPathComponent == null) {
        return null;
    }

    return getVariantRaw(lastPathComponent);
}

function getUrlPathComponents(): string[] {
    const url = location.pathname.trim();

    // Skip empty paths
    if (url.length === 0 || url === "/") {
        return [];
    }

    // Break into path components
    return url
        .split('/')
        .filter(p => p.trim().length > 0);
}

function getVariantRaw(hash: string): Nullable<GameVariant> {

    hash = hash?.trim() ?? "";
    if (hash.length != 2) {
        return null;
    }
    const hashUpper = hash.toUpperCase();
    return allVariants.find(variant => variant === hashUpper);
}


function getVariantFromHashParams(...hashParameters: HashParameters[]): Nullable<GameVariant> {
    for (const parameters of hashParameters) {
        if (typeof parameters["variant"] === "undefined") {
            continue;
        }
        const variant = getVariantRaw(parameters["variant"] as string);
        if (variant) {
            return variant;
        }
    }
    return null;
}

function initHashChange() {
    window.addEventListener('hashchange', onHashChange);
}

export function setQueryInUrl(query: Nullable<string>) {
    const params = getHashParameters();
    const lastQuery = typeof params["query"] !== "undefined"
        ? params["query"]
        : null;
    if (query === lastQuery) {
        return;
    }

    const similarQuery = levenshtein(query?.toLowerCase() ?? "", (lastQuery as string)?.toLowerCase() ?? "") <= 1;

    if (query == null) {
        delete params["query"];
    } else {
        params["query"] = query;
    }
    lastHash = calculateUrlHash(params);
    if (window.location.hash.indexOf("query=") >= 0 && similarQuery) {
        history.replaceState(null, "", location.href.split("?", 2)[0].split("#")[0] + "#" + lastHash);
    } else {
        location.hash = lastHash;
    }
}

export function getInitialQuery(): Nullable<string> {
    return getUrlParameter("query") as string;
}